import React, { useState } from 'react';
import { Row, Col, Menu, Dropdown, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import moment from 'moment';
import TokenIcon from '../TokenIcon';
import CopyWrapper from '../CopyWrapper';
import formatCurrency from '../../utils/currencyFormatter';
import assetFormatter from '../../utils/assetFormatter';
import { ReactComponent as ArrowDown } from '../../img/general-icon/arrow-down.svg';
import ReactPaginate from 'react-paginate';
import { ReactComponent as CopyGrayIcon } from '../../img/default-svg/copy.svg';
import { ReactComponent as ArrowLong } from '../../img/wallets/arrow-long.svg';
import { ReactComponent as ArrowLong1 } from '../../img/wallets/arrow-long-1.svg';
import moreIcon from '../../img/default-svg/more.svg';
import moreIconInex from '../../img/inex/more.svg';
import './WalletsNewList.scss';
import '../Pagination/Pagination.scss';
import { tokenNodeTypeObj, tokenTypeEnd } from '../../utils/tokenNodeTypes';
import { identity } from '../../utils/getIdentity';
import { cryptoApi } from '../../service/cryptopay-api';
import { adminApi } from '../../service/admin-api';
import { notificationsErrorShow } from '../../redux/actions/notifications';
import ReplenishModal from '../ReplenishModal/ReplenishModal';
import WalletDeleteModal from './WalletDeleteModal';
import AdminDataModal from './AdminDataModal';
import PrivateKeyModal from './PrivateKeyModal';
import { merchantWalletsSetPrivateKeyNoTwoFa } from '../../redux/actions/merchantWallets';

const WALLET_DATA_LABELS = {
  key: 'privateKey',
  mnemonic: 'mnemonicPhrase',
  passphrase: 'passphrase',
};

const BTC_CODE = '5edf2767c9ca4d5a342bf8ac';

const walletCurrencyId = (w) => w?.currencyId ?? w?.balances?.[0]?.currency?.id;

const ClientsNewList = ({
  wallets,
  page,
  pages,
  fetching,
  merchantId,
  networkFilter,
  currencyIds,
  getMoreMerchantWallets,
  search,
  hideZeroCheckbox,
  walletVersion,
  deleteMerchantWallet,
  getWalletPrivateKey,
  getWalletMnemonicPhrase,
  getPassphrase,
  resetTwoFaState,
  countItem,
  walletsFilters,
  refreshWallets,
  typeWallet = 'user',
  type,
  userId,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [expandedItems, setExpandedItems] = useState({});
  const [walletId, setWalletId] = useState('');
  const [walletAddress, setWalletAddress] = useState('');
  const [walletCurrency, setWalletCurrency] = useState('');
  const [isReplenishModalOpen, setIsReplenishModalOpen] = useState(false);
  const [isKeyModalOpen, setIsKeyModalOpen] = useState(false);
  const [showWalletBy, setShowWalletBy] = useState(null);
  const [adminData, setAdminData] = useState('');
  const [adminDataTitle, setAdminDataTitle] = useState('');
  const [isOpenAdminModal, setIsOpenAdminModal] = useState(false);

  const fetchParams = {
    typeNetwork: networkFilter,
    walletVersion,
    order: 'DESC',
    limit: 10,
    currencyIds: currencyIds?.join(',') || undefined,
    typeWallet,
    showTokens: false,
    search: search || undefined,
    positiveBalance: hideZeroCheckbox,
  };

  const toggleExpanded = (address) => {
    setExpandedItems((prev) => ({ ...prev, [address]: !prev[address] }));
  };

  const getGeneralAmount = (array) => {
    const balance = array?.reduce((acc, cur) => acc + cur?.balance?.usd, 0);
    return balance;
  };

  const handlePageChange = (selected) => {
    getMoreMerchantWallets(merchantId, { ...fetchParams, page: selected.selected + 1 });
  };

  const openNotification = () => {
    notification.success({
      message: t('copied'),
      duration: 2,
    });
  };

  const handleReplenishButton = (address, currency, isModalOpen) => {
    setWalletAddress(address);
    setWalletCurrency(currency);
    setIsReplenishModalOpen(isModalOpen);
  };

  const openModal = (id) => {
    setWalletId(id);
    setIsKeyModalOpen(true);
  };

  const closeModal = () => {
    dispatch(merchantWalletsSetPrivateKeyNoTwoFa(''));
    setIsKeyModalOpen(false);
    resetTwoFaState(false);
  };

  const getAdminData = async (type, wallet) => {
    try {
      const response = await adminApi.getAdminWalletData(type, userId, merchantId, wallet._id);
      if (response && response?.data) {
        setAdminData(response?.data);

        if (type === 'private-key') {
          setAdminDataTitle('Private key');
        } else if (type === 'passphrase') {
          setAdminDataTitle('Passphrase');
        } else {
          setAdminDataTitle('Mnemonic phrase');
        }

        setIsOpenAdminModal(true)
      }
    } catch (error) {
      setAdminData('');
      setAdminDataTitle('');
      dispatch(notificationsErrorShow({ message: error?.data?.message || '' }));
    }
  };

  const closeAdminModal = () => {
    setIsOpenAdminModal(false);
    setAdminData('');
    setAdminDataTitle('');
  }

  const getUserData = (dataType, wallet) => {
    if (type === 'admin') {
      getAdminData(dataType, wallet);
      return;
    }
    if (dataType === 'private-key') {
      getWalletPrivateKey(merchantId, wallet._id);
      setShowWalletBy(WALLET_DATA_LABELS.key);
    } else if (dataType === 'passphrase') {
      getPassphrase(merchantId, wallet._id);
      setShowWalletBy(WALLET_DATA_LABELS.passphrase);
    } else if (dataType === 'mnemonic') {
      getWalletMnemonicPhrase(merchantId, wallet._id);
      setShowWalletBy(WALLET_DATA_LABELS.mnemonic);
    }
    openModal(wallet._id);
  };

  const changeToSegwit = async (wallet) => {
    try {
      await cryptoApi.changeToSegwit(merchantId, wallet?._id);
      if (refreshWallets) {
        refreshWallets();
      }
    } catch (error) {
      console.error(error);
    }
  };

  const moreMenu = (item) => (
    <Menu>
      {type !== 'admin' && (
        <Menu.Item
          key="1"
          onClick={() => handleReplenishButton(item.address, item.currency || item.chainSymbol, true)}
        >
          <div>{t('merchants.replenish')}</div>
        </Menu.Item>
      )}
      <Menu.Item key="2">
        <div onClick={() => getUserData('private-key', item)}>{t('showPrivateKey')}</div>
      </Menu.Item>
      <Menu.Item key="5">
        <div onClick={() => getUserData('passphrase', item)}>{t('showPassphrase')}</div>
      </Menu.Item>
      {walletCurrencyId(item) !== BTC_CODE ? (
        <Menu.Item key="4">
          <div onClick={() => getUserData('mnemonic', item)}>{t('showMnemonic')}</div>
        </Menu.Item>
      ) : null}
      {type !== 'admin' && (
        <Menu.Item key="3">
          <WalletDeleteModal
            merchantId={merchantId}
            walletId={item._id}
            walletAddress={item.address}
            deleteMerchantWallet={deleteMerchantWallet}
            typeWallet={typeWallet}
            page={page}
            pages={pages}
            countItem={countItem}
            walletsFilters={walletsFilters}
            networkFilter={networkFilter}
          />
        </Menu.Item>
      )}
      {(item?.nodeType === 'btc' || item?.balances?.[0]?.currency?.nodeType === 'btc') && !item?.isSegwit ? (
        <Menu.Item key="segwit" onClick={() => changeToSegwit(item)}>
          Change to Native SegWit
        </Menu.Item>
      ) : null}
    </Menu>
  );

  if (fetching) {
    return (
      <div className="admin-wallet">
        <div className="admin-wallet__loader">
          <div className="admin-wallet__spinner" />
        </div>
      </div>
    );
  }

  if (!wallets?.length) {
    return null;
  }

  return (
    <React.Fragment>
      <ReplenishModal
        walletAddress={walletAddress}
        walletCurrency={walletCurrency}
        isReplenishModalOpen={isReplenishModalOpen}
        handleReplenishButton={handleReplenishButton}
      />
      <PrivateKeyModal
        merchantId={merchantId}
        walletId={walletId}
        setWalletId={setWalletId}
        isKeyModalOpen={isKeyModalOpen}
        closeModal={closeModal}
        openNotification={openNotification}
        getWalletPrivateKey={getWalletPrivateKey}
        showWalletBy={showWalletBy}
        getWalletMnemonicPhrase={getWalletMnemonicPhrase}
        getPassphrase={getPassphrase}
      />
      <AdminDataModal
        title={adminDataTitle}
        visible={isOpenAdminModal}
        close={closeAdminModal}
        data={adminData}
      />
      <div className="admin-wallet">
        <div className="admin-wallet__content">
          <Row align="middle" className="admin-wallet__rowTitle">
            <Col span={1} />
            <Col span={10}>{t('address')}</Col>
            <Col span={4}>{t('amount')}</Col>
            <Col span={3}>Network</Col>
            <Col span={4}>{t('dataTime')}</Col>
            <Col span={1} />
          </Row>
          {wallets.map((item) => (
            <Row align="middle" className="admin-wallet__rowBody" key={item?._id || item?.address}>
              <Col
                className="admin-wallet__end"
                span={1}
                onClick={() => toggleExpanded(item.address)}
                style={{ cursor: 'pointer', marginTop: '-3px' }}
              >
                {item?.balances?.length > 0 && (
                  <ArrowDown
                    color="#80827E"
                    style={{
                      transform: expandedItems[item.address] ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                    }}
                  />
                )}
              </Col>
              <Col span={10} className="admin-wallet__address">
                <TokenIcon tokenName={item?.chainSymbol} />
                <CopyWrapper copyContent={item?.address}>{item?.address}</CopyWrapper>
                <CopyWrapper copyContent={item?.address}>
                  <CopyGrayIcon />
                </CopyWrapper>
              </Col>
              <Col span={4}>{formatCurrency(+getGeneralAmount(item?.balances))}</Col>
              <Col span={3}>{item?.chainSymbol?.toUpperCase() || '-'}</Col>
              <Col span={4}>{item?.createdAt ? moment(item.createdAt).format('MMM D, YYYY, HH:mm') : '-'}</Col>
              <Col className="admin-wallet__end" span={1}>
                <Dropdown overlay={moreMenu(item)} trigger={['click']}>
                  <div
                    style={{ display: 'flex', justifyContent: 'flex-end', cursor: 'pointer' }}
                    role="presentation"
                  >
                    <img
                      src={identity === 'inex' ? moreIconInex : moreIcon}
                      alt="options"
                      style={{ width: 16, height: 16 }}
                    />
                  </div>
                </Dropdown>
              </Col>

              <div className="list__row">
                {expandedItems[item?.address] &&
                  item?.balances?.map((asset, index) => (
                    <Row align="middle" key={asset.id}>
                      <Col span={2}>{index === 0 ? <ArrowLong /> : <ArrowLong1 />}</Col>
                      <Col
                        span={8}
                        className={`tokens admin-wallet__relative ${index !== 0 && 'tokens__big'}`}
                      >
                        <TokenIcon className="" tokenName={asset?.currency?.name} />
                        {asset?.currency?.name === 'Binance-Peg BTCB Token' ? 'BTCB' : asset?.currency?.name}{' '}
                        {asset?.currency?.currencyType === 'token' ? tokenNodeTypeObj[asset?.currency?.nodeType] : null}
                        {tokenTypeEnd(asset?.currency?.currencyType, asset?.currency?.nodeType, asset?.currency?.name)}
                      </Col>
                      <Col className="admin-wallet__left" span={4}>
                        <span>
                          {assetFormatter(asset?.balance?.value, asset?.currency?.name)} {asset?.currency?.name}
                        </span>
                        <span className="admin-wallet__left--amount">({formatCurrency(+asset?.balance?.usd)})</span>
                      </Col>
                    </Row>
                  ))}
              </div>
            </Row>
          ))}
          {pages > 1 && (
            <ReactPaginate
              pageCount={pages}
              pageRangeDisplayed={5}
              marginPagesDisplayed={2}
              onPageChange={handlePageChange}
              containerClassName="pagination"
              activeClassName="active"
              previousLabel="«"
              nextLabel="»"
              breakLabel="..."
              forcePage={Math.max(0, (page || 1) - 1)}
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};

export default ClientsNewList;
